import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { Pencil, Trash2, LogOut, Package, FileText, Send, Copy, Settings, Search, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { value: 'amazon', label: 'أمازون' },
  { value: 'noon', label: 'نون' },
  { value: 'namshi', label: 'نمشي' },
  { value: 'aliexpress', label: 'علي إكسبريس' }
];

const bannerTabs = [
  { key: 'home', label: 'جميع العروض' },
  { key: 'amazon', label: 'أمازون' },
  { key: 'noon', label: 'نون' },
  { key: 'namshi', label: 'نمشي' },
  { key: 'aliexpress', label: 'علي إكسبريس' }
];

const staticPages = [
  { slug: 'about', title: 'من نحن' },
  { slug: 'privacy', title: 'سياسة الخصوصية' },
  { slug: 'disclaimer', title: 'إخلاء المسؤولية' }
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [pages, setPages] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [postingToTelegram, setPostingToTelegram] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [bannerKey, setBannerKey] = useState('home');
  const [bannerData, setBannerData] = useState({});
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerLink, setBannerLink] = useState('');
  const [bannerSaving, setBannerSaving] = useState(false);
  const [requestsList, setRequestsList] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [replyForm, setReplyForm] = useState({ name: '', email: '', message: '' });
  const [sendingReply, setSendingReply] = useState({});
  const [visibleRequests, setVisibleRequests] = useState(6);
  const [requestIntroText, setRequestIntroText] = useState('');
  const [savingRequestIntro, setSavingRequestIntro] = useState(false);
  const replyFieldRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    image_url: '',
    affiliate_link: '',
    price: '',
    discount_code: '',
    category: '',
    description: ''
  });
  const [pageFormData, setPageFormData] = useState({
    slug: '',
    title: '',
    content: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchProducts();
    fetchPages();
    fetchRequests();
    fetchRequestIntro();
  }, [navigate]);

  useEffect(() => {
    fetchBanner(bannerKey);
  }, [bannerKey]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchProducts = async () => {
    try {
      const pageSize = 200;
      let skip = 0;
      const allProducts = [];

      while (true) {
        const response = await axios.get(`${API}/products`, {
          params: { limit: pageSize, skip }
        });
        const batch = Array.isArray(response.data) ? response.data : [];
        allProducts.push(...batch);

        if (batch.length < pageSize) {
          break;
        }
        skip += pageSize;
      }

      setProducts(allProducts);
    } catch (error) {
      toast.error('خطأ في تحميل المنتجات');
    }
  };

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${API}/pages`);
      setPages(response.data);
    } catch (error) {
      console.error('خطأ في تحميل الصفحات:', error);
    }
  };

  const fetchBanner = async (key = bannerKey) => {
    try {
      const response = await axios.get(`${API}/settings/banner`, { params: { key } });
      const normalized = {
        ...response.data,
        is_active: response.data?.is_active !== false
      };
      setBannerData((prev) => ({ ...prev, [key]: normalized }));
      setBannerLink(normalized?.link_url || '');
    } catch (error) {
      console.error('خطأ في تحميل البنر:', error);
    }
  };

  const handleBannerTabChange = (key) => {
    setBannerKey(key);
    setBannerFile(null);
    const existingBanner = bannerData[key];
    if (existingBanner) {
      setBannerLink(existingBanner?.link_url || '');
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/requests`, { headers: getAuthHeader() });
      setRequestsList(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('انتهت جلسة الدخول');
        handleLogout();
      } else {
        toast.error('خطأ في تحميل الطلبات');
      }
    }
  };

  const fetchRequestIntro = async () => {
    try {
      const response = await axios.get(`${API}/settings/request-intro`);
      setRequestIntroText(response.data?.text || '');
    } catch (error) {
      console.error('Failed to load request intro text:', error);
    }
  };

  const resolveBannerUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL || ''}${url}`;
  };

  const handleBannerSave = async (e) => {
    e.preventDefault();
    setBannerSaving(true);
    try {
      const currentKey = bannerKey;
      const existingBanner = bannerData[currentKey];
      let imageUrl = existingBanner?.image_url || '';
      if (bannerFile) {
        const formData = new FormData();
        formData.append('file', bannerFile);
        const uploadRes = await axios.post(
          `${API}/uploads/banner`,
          formData,
          { headers: getAuthHeader() }
        );
        imageUrl = uploadRes.data?.image_url || '';
      }

      if (!imageUrl) {
        toast.error('يرجى رفع صورة البنر');
        return;
      }

      const updateRes = await axios.put(
        `${API}/settings/banner`,
        { image_url: imageUrl, link_url: bannerLink || '', is_active: existingBanner?.is_active !== false },
        { headers: getAuthHeader(), params: { key: currentKey } }
      );
      setBannerData((prev) => ({ ...prev, [currentKey]: updateRes.data }));
      setBannerLink(updateRes.data?.link_url || '');
      setBannerFile(null);
      toast.success('تم تحديث البنر بنجاح');
    } catch (error) {
      toast.error('فشل تحديث البنر');
    } finally {
      setBannerSaving(false);
    }
  };

  const handleBannerToggle = async (checked) => {
    const currentKey = bannerKey;
    const existingBanner = bannerData[currentKey];
    if (!existingBanner) {
      setBannerData((prev) => ({
        ...prev,
        [currentKey]: { image_url: '', link_url: bannerLink || '', is_active: checked }
      }));
      return;
    }

    try {
      const updateRes = await axios.put(
        `${API}/settings/banner`,
        { image_url: existingBanner.image_url || '', link_url: existingBanner.link_url || '', is_active: checked },
        { headers: getAuthHeader(), params: { key: currentKey } }
      );
      setBannerData((prev) => ({ ...prev, [currentKey]: updateRes.data }));
      toast.success(checked ? 'تم إظهار البنر' : 'تم إخفاء البنر');
    } catch (error) {
      toast.error('فشل تحديث حالة البنر');
    }
  };

  const handleBannerDelete = async () => {
    const currentKey = bannerKey;
    const existingBanner = bannerData[currentKey];
    if (!existingBanner?.image_url) {
      toast.error('لا يوجد بنر لحذفه');
      return;
    }
    const confirmed = window.confirm('هل تريد حذف البنر الحالي؟');
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/settings/banner`, { headers: getAuthHeader(), params: { key: currentKey } });
      setBannerData((prev) => ({ ...prev, [currentKey]: null }));
      setBannerFile(null);
      setBannerLink('');
      toast.success('تم حذف البنر بنجاح');
    } catch (error) {
      toast.error('فشل حذف البنر');
    }
  };

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const nameMatch = product.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const categoryMatch = categoryFilter === 'all' || product.category === categoryFilter;
      return nameMatch && categoryMatch;
    });
  }, [products, searchTerm, categoryFilter]);

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    try {
      // إرسال البيانات كـ JSON عادي (وهذا يطابق السيرفر الجديد)
      if (editingProduct) {
        await axios.put(
          `${API}/products/${editingProduct.id}`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('تم تحديث المنتج بنجاح');
      } else {
        await axios.post(
          `${API}/products`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('تم إضافة المنتج بنجاح');
      }
      resetProductForm();
      fetchProducts();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('انتهت جلسة الدخول');
        handleLogout();
      } else {
        toast.error('حدث خطأ في حفظ المنتج');
      }
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      image_url: product.image_url,
      affiliate_link: product.affiliate_link,
      price: product.price,
      discount_code: product.discount_code || '',
      category: product.category,
      description: product.description
    });
  };

  const handleDeleteProduct = async () => {
    try {
      await axios.delete(
        `${API}/products/${deleteDialog.id}`,
        { headers: getAuthHeader() }
      );
      toast.success('تم حذف المنتج بنجاح');
      setDeleteDialog({ open: false, id: null });
      fetchProducts();
    } catch (error) {
      toast.error('حدث خطأ في حذف المنتج');
    }
  };

  const resetProductForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      image_url: '',
      affiliate_link: '',
      price: '',
      discount_code: '',
      category: '',
      description: ''
    });
  };

  const handleSubmitPage = async (e) => {
    e.preventDefault();
    try {
      if (editingPage) {
        await axios.put(
          `${API}/pages/${editingPage.slug}`,
          { title: pageFormData.title, content: pageFormData.content },
          { headers: getAuthHeader() }
        );
        toast.success('تم تحديث الصفحة بنجاح');
      } else {
        await axios.post(
          `${API}/pages`,
          pageFormData,
          { headers: getAuthHeader() }
        );
        toast.success('تم إضافة الصفحة بنجاح');
      }
      resetPageForm();
      fetchPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ في حفظ الصفحة');
    }
  };

  const handleSaveRequestIntro = async (event) => {
    event.preventDefault();
    setSavingRequestIntro(true);
    try {
      await axios.put(
        `${API}/settings/request-intro`,
        { text: requestIntroText },
        { headers: getAuthHeader() }
      );
      toast.success('تم حفظ نص صفحة الطلب');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('انتهت جلسة الدخول');
        handleLogout();
      } else {
        toast.error('فشل حفظ نص صفحة الطلب');
      }
    } finally {
      setSavingRequestIntro(false);
    }
  };

  const handleEditPage = (page) => {
    setEditingPage(page);
    setPageFormData({
      slug: page.slug,
      title: page.title,
      content: page.content
    });
  };

  const resetPageForm = () => {
    setEditingPage(null);
    setPageFormData({
      slug: '',
      title: '',
      content: ''
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_type');
    navigate('/admin');
  };

  const handlePostToTelegram = async (productId) => {
    setPostingToTelegram({ ...postingToTelegram, [productId]: true });
    try {
      const response = await axios.post(
        `${API}/telegram/post/${productId}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`✅ ${response.data.message}`);
    } catch (error) {
      toast.error(`❌ ${error.response?.data?.detail || 'فشل النشر'}`);
    } finally {
      setPostingToTelegram({ ...postingToTelegram, [productId]: false });
    }
  };

  const handleCopyContent = (product) => {
    const content = `⭐ ${product.name}\n\n📝 ${product.description}\n\n💰 السعر: ${product.price}${product.discount_code ? `\n🏷️ كود الخصم: ${product.discount_code}` : ''}\n\n🔗 رابط المنتج: ${product.affiliate_link}\n\n#عروض #تسوق_اونلاين #${product.category}`;
    navigator.clipboard.writeText(content).then(() => {
      toast.success('✅ تم نسخ المحتوى');
    }).catch(() => {
      toast.error('❌ فشل النسخ');
    });
  };

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('ar-SA');
  };

  const handleSelectRequest = async (request) => {
    setSelectedRequest(request);
    setReplyForm({
      name: request.customer_name || '',
      email: request.email || '',
      message: request.reply_message || ''
    });
    if (request.status === 'new') {
      try {
        await axios.patch(
          `${API}/requests/${request.id}`,
          { status: 'open' },
          { headers: getAuthHeader() }
        );
        fetchRequests();
      } catch (error) {
        console.error('خطأ في تحديث حالة الطلب:', error);
      }
    }
    setTimeout(() => {
      replyFieldRef.current?.focus();
    }, 100);
  };

  const handleSendReply = async () => {
    const message = replyForm.message.trim();
    if (!message) {
      toast.error('يرجى كتابة الرد');
      return;
    }
    if (!replyForm.email.trim()) {
      toast.error('يرجى إدخال البريد الإلكتروني');
      return;
    }

    if (!selectedRequest) {
      setSendingReply((prev) => ({ ...prev, direct: true }));
      try {
        await axios.post(
          `${API}/requests/reply-direct`,
          { message, to_email: replyForm.email.trim() },
          { headers: getAuthHeader() }
        );
        toast.success('تم إرسال الرسالة بنجاح');
        setReplyForm({ name: '', email: '', message: '' });
      } catch (error) {
        toast.error(error.response?.data?.detail || 'فشل إرسال الرسالة');
      } finally {
        setSendingReply((prev) => ({ ...prev, direct: false }));
      }
      return;
    }

    setSendingReply((prev) => ({ ...prev, [selectedRequest.id]: true }));
    try {
      await axios.post(
        `${API}/requests/${selectedRequest.id}/reply`,
        { message, to_email: replyForm.email.trim() },
        { headers: getAuthHeader() }
      );
      toast.success('تم إرسال الرد بنجاح');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال الرد');
    } finally {
      setSendingReply((prev) => ({ ...prev, [selectedRequest.id]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <div className="border-b bg-white">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">لوحة التحكم</h1>
          <div className="flex gap-2">
            <Button onClick={handleLogout} variant="outline" data-testid="logout-button">
              <LogOut className="ml-2 h-4 w-4" />
              تسجيل الخروج
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="products" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <Link to="/admin/telegram-settings" className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all hover:bg-muted">
              <Settings className="ml-2 h-4 w-4" />
              إعدادات تيليجرام
            </Link>
            <TabsTrigger value="pages">
              <FileText className="ml-2 h-4 w-4" />
              الصفحات
            </TabsTrigger>
            <TabsTrigger value="requests">
              <Mail className="ml-2 h-4 w-4" />
              الطلبات
            </TabsTrigger>
            <TabsTrigger value="products">
              <Package className="ml-2 h-4 w-4" />
              المنتجات
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <div className="md:col-span-4 space-y-6"> {/* تم إضافة space-y-6 للفصل بين البطاقات */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-right">
                      {editingProduct ? 'تعديل منتج' : 'إضافة منتج جديد'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmitProduct} className="space-y-4 text-right">
                      {/* حقول الإدخال كما هي تماماً */}
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-right">اسم المنتج</Label>
                        <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="admin-input text-right" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="category" className="text-right">التصنيف</Label>
                        <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })} required>
                          <SelectTrigger className="admin-input text-right"><SelectValue placeholder="اختر التصنيف" /></SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (<SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="image_url" className="text-right">رابط الصورة</Label>
                        <Input id="image_url" type="url" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} required className="admin-input text-right" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="price" className="text-right">السعر</Label>
                        <Input id="price" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required className="admin-input text-right" placeholder="مثال: 299 ريال" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="discount_code" className="text-right">كود الخصم (اختياري)</Label>
                        <Input id="discount_code" value={formData.discount_code} onChange={(e) => setFormData({ ...formData, discount_code: e.target.value })} className="admin-input text-right" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="description" className="text-right">وصف المنتج</Label>
                        <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required className="admin-input min-h-20 text-right" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="affiliate_link" className="text-right">رابط الأفلييت</Label>
                        <Input id="affiliate_link" type="url" value={formData.affiliate_link} onChange={(e) => setFormData({ ...formData, affiliate_link: e.target.value })} required className="admin-input text-right" />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" className="btn-primary flex-1">{editingProduct ? 'تحديث' : 'إضافة'}</Button>
                        {editingProduct && (<Button type="button" variant="outline" onClick={resetProductForm}>إلغاء</Button>)}
                      </div>
                    </form>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-right">بنرات الموقع</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Tabs value={bannerKey} onValueChange={handleBannerTabChange}>
                      <TabsList className="flex w-full flex-row-reverse gap-2">
                        {bannerTabs.map((tab) => (
                          <TabsTrigger key={tab.key} value={tab.key} className="flex-1">
                            {tab.label}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                    </Tabs>
                    <form onSubmit={handleBannerSave} className="space-y-4 text-right mt-4">
                      <div className="flex items-center justify-between flex-row-reverse">
                        <Label className="text-right">إظهار البنر</Label>
                        <Switch
                          checked={bannerData[bannerKey]?.is_active !== false}
                          onCheckedChange={handleBannerToggle}
                        />
                      </div>
                      {bannerData[bannerKey]?.image_url && (
                        <div className="rounded-xl overflow-hidden border">
                          <img
                            src={resolveBannerUrl(bannerData[bannerKey]?.image_url)}
                            alt={`بنر ${bannerTabs.find((tab) => tab.key === bannerKey)?.label || ''}`}
                            className="w-full h-auto object-cover"
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label htmlFor="banner_file" className="text-right">صورة البنر</Label>
                        <Input
                          id="banner_file"
                          type="file"
                          accept="image/*"
                          onChange={(e) => setBannerFile(e.target.files?.[0] || null)}
                          className="admin-input text-right"
                        />
                        <p className="text-xs text-muted-foreground text-right">الحد الأقصى 2MB</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="banner_link" className="text-right">رابط البنر (اختياري)</Label>
                        <Input
                          id="banner_link"
                          type="url"
                          value={bannerLink}
                          onChange={(e) => setBannerLink(e.target.value)}
                          placeholder="https://..."
                          className="admin-input text-right"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" className="flex-1 btn-primary" disabled={bannerSaving}>
                          {bannerSaving ? 'جاري الحفظ...' : 'حفظ البنر'}
                        </Button>
                        <Button type="button" variant="outline" className="flex-1" onClick={handleBannerDelete}>
                          حذف البنر
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>

              </div>

              <div className="md:col-span-8">
                <Card>
                  <CardHeader className="flex flex-col md:flex-row-reverse md:items-center md:justify-between space-y-3 md:space-y-0 pb-4">
                    <CardTitle className="text-right">قائمة المنتجات ({filteredProducts.length})</CardTitle>
                    <div className="relative w-full max-w-sm">
                      <Search className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input placeholder="ابحث باسم المنتج..." className="pr-9" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                  </CardHeader>
                  <div className="px-6 pb-4 flex flex-wrap gap-2">
                    <Button variant={categoryFilter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setCategoryFilter('all')}>الكل</Button>
                    {categories.map(cat => (<Button key={cat.value} variant={categoryFilter === cat.value ? 'default' : 'outline'} size="sm" onClick={() => setCategoryFilter(cat.value)}>{cat.label}</Button>))}
                  </div>
                  <CardContent>
                    <div className="space-y-4 max-h-[700px] overflow-y-auto">
                      {filteredProducts.map((product) => (
                        <div key={product.id} className="flex flex-col md:flex-row-reverse md:items-start gap-4 p-4 border rounded-lg hover:bg-accent/50 transition-colors text-right">
                          <img src={product.image_url} alt={product.name} className="w-20 h-20 object-cover rounded-lg" />
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg mb-1">{product.name}</h3>
                            <p className="text-sm text-muted-foreground mb-1 line-clamp-2">{product.description}</p>
                            <div className="flex flex-wrap items-center gap-4 text-sm mb-2">
                              <span className="font-medium text-primary">{product.price}</span>
                              <span className="text-muted-foreground">{categories.find(c => c.value === product.category)?.label}</span>
                              <span className="text-muted-foreground">{product.views} مشاهدة</span>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <Button size="sm" variant="outline" onClick={() => handlePostToTelegram(product.id)} disabled={postingToTelegram[product.id]} className="text-xs">
                                {postingToTelegram[product.id] ? '⏳ جارٍ النشر...' : <><Send className="ml-1 h-3 w-3" /> تيليجرام</>}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleCopyContent(product)} className="text-xs">
                                <Copy className="ml-1 h-3 w-3" /> نسخ المحتوى
                              </Button>
                            </div>
                          </div>
                          <div className="flex gap-2 md:flex-col">
                            <Button size="icon" variant="outline" onClick={() => handleEditProduct(product)}><Pencil className="h-4 w-4" /></Button>
                            <Button size="icon" variant="destructive" onClick={() => setDeleteDialog({ open: true, id: product.id })}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      ))}
                      {filteredProducts.length === 0 && <p className="text-center text-muted-foreground py-8">لا توجد منتجات تطابق البحث</p>}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="requests">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-right">طلبات العملاء ({requestsList.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {requestsList.slice(0, visibleRequests).map((request) => {
                      const platformLabel = categories.find((cat) => cat.value === request.platform)?.label || request.platform;
                      const statusLabel = request.status === 'replied' ? 'تم الرد' : request.status === 'open' ? 'مفتوح' : 'جديد';
                      return (
                        <button
                          key={request.id}
                          type="button"
                          onClick={() => handleSelectRequest(request)}
                          className={`w-full text-right p-4 border rounded-xl transition-colors ${selectedRequest?.id === request.id ? 'border-primary bg-primary/5' : 'hover:bg-accent/50'}`}
                        >
                          <div className="flex flex-col md:flex-row-reverse md:items-start md:justify-between gap-2">
                            <div>
                              <h3 className="font-semibold text-lg mb-1">{request.product_name}</h3>
                              <p className="text-sm text-muted-foreground">{platformLabel} • {request.customer_name} • {request.email}</p>
                              {request.product_url && (
                                <a
                                  href={request.product_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-primary underline"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  رابط المنتج
                                </a>
                              )}
                              {request.notes && (
                                <p className="text-sm text-muted-foreground mt-2">{request.notes}</p>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground text-left">
                              <span className="inline-block bg-muted px-2 py-1 rounded-md">
                                {statusLabel}
                              </span>
                              <div className="mt-2">{formatDate(request.created_at)}</div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                    {requestsList.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">لا توجد طلبات حالياً</p>
                    )}
                    {requestsList.length > visibleRequests && (
                      <Button variant="outline" className="w-full" onClick={() => setVisibleRequests((prev) => prev + 6)}>
                        عرض المزيد
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-right">الرد على الطلب</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex flex-col md:flex-row-reverse gap-4">
                      <div className="space-y-2 flex-1">
                        <Label htmlFor="reply_name" className="text-right">الاسم</Label>
                        <Input
                          id="reply_name"
                          value={replyForm.name}
                          onChange={(event) => setReplyForm({ ...replyForm, name: event.target.value })}
                          placeholder="اسم صاحب الطلب"
                          className="admin-input text-right"
                        />
                      </div>
                      <div className="space-y-2 flex-1">
                        <Label htmlFor="reply_email" className="text-right">البريد الإلكتروني</Label>
                        <Input
                          id="reply_email"
                          type="email"
                          value={replyForm.email}
                          onChange={(event) => setReplyForm({ ...replyForm, email: event.target.value })}
                          placeholder="example@email.com"
                          className="admin-input text-right"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="reply_message" className="text-right">الرد</Label>
                      <Textarea
                        id="reply_message"
                        ref={replyFieldRef}
                        value={replyForm.message}
                        onChange={(event) => setReplyForm({ ...replyForm, message: event.target.value })}
                        className="admin-input min-h-28 text-right"
                        placeholder="اكتب الرد هنا"
                      />
                    </div>

                    <Button
                      type="button"
                      className="btn-primary"
                      disabled={sendingReply[selectedRequest?.id] || sendingReply.direct}
                      onClick={handleSendReply}
                    >
                      {sendingReply[selectedRequest?.id] || sendingReply.direct ? 'جاري الإرسال...' : 'إرسال الرد'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* تبويب الصفحات (كما هو بدون تغيير) */}
          <TabsContent value="pages">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <div className="md:col-span-5 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-right">نص أعلى صفحة طلب الكوبون</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSaveRequestIntro} className="space-y-4 text-right">
                      <div className="space-y-2">
                        <Label htmlFor="request_intro_text" className="text-right">النص التوضيحي</Label>
                        <Textarea
                          id="request_intro_text"
                          value={requestIntroText}
                          onChange={(event) => setRequestIntroText(event.target.value)}
                          className="admin-input min-h-40 text-right"
                          placeholder="اكتب النص الذي يظهر للزائر أعلى نموذج طلب الكوبون..."
                        />
                      </div>
                      <p className="text-xs text-muted-foreground text-right">
                        يظهر هذا النص أعلى نموذج صفحة /request ويدعم الأسطر المتعددة.
                      </p>
                      <Button type="submit" className="btn-primary" disabled={savingRequestIntro}>
                        {savingRequestIntro ? 'جاري الحفظ...' : 'حفظ النص'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="text-right">{editingPage ? 'تعديل صفحة' : 'إضافة صفحة جديدة'}</CardTitle></CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmitPage} className="space-y-4 text-right">
                      <div className="space-y-2">
                        <Label htmlFor="page_slug" className="text-right">معرف الصفحة (Slug)</Label>
                        <Select value={pageFormData.slug} onValueChange={(value) => { const page = staticPages.find(p => p.slug === value); setPageFormData({ ...pageFormData, slug: value, title: page?.title || '' }); }} disabled={!!editingPage} required>
                          <SelectTrigger className="admin-input text-right"><SelectValue placeholder="اختر الصفحة" /></SelectTrigger>
                          <SelectContent>{staticPages.map((page) => (<SelectItem key={page.slug} value={page.slug}>{page.title}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2"><Label htmlFor="page_title" className="text-right">عنوان الصفحة</Label><Input id="page_title" value={pageFormData.title} onChange={(e) => setPageFormData({ ...pageFormData, title: e.target.value })} required className="admin-input text-right" /></div>
                      <div className="space-y-2"><Label htmlFor="page_content" className="text-right">محتوى الصفحة</Label><Textarea id="page_content" value={pageFormData.content} onChange={(e) => setPageFormData({ ...pageFormData, content: e.target.value })} required className="admin-input min-h-64 text-right" /></div>
                      <div className="flex gap-2"><Button type="submit" className="btn-primary flex-1">{editingPage ? 'تحديث' : 'إضافة'}</Button>{editingPage && (<Button type="button" variant="outline" onClick={resetPageForm}>إلغاء</Button>)}</div>
                    </form>
                  </CardContent>
                </Card>
              </div>
              <div className="md:col-span-7">
                <Card>
                  <CardHeader><CardTitle className="text-right">الصفحات المتاحة</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {pages.map((page) => (
                        <div key={page.id} className="p-4 border rounded-lg hover:bg-accent/50 transition-colors text-right">
                          <div className="flex items-start justify-between flex-row-reverse">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg mb-1">{page.title}</h3>
                              <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{page.content}</p>
                              <span className="text-xs text-muted-foreground">/{page.slug}</span>
                            </div>
                            <Button size="icon" variant="outline" onClick={() => handleEditPage(page)}><Pencil className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      ))}
                      {pages.length === 0 && <p className="text-center text-muted-foreground py-8">لا توجد صفحات</p>}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader><AlertDialogTitle>هل أنت متأكد؟</AlertDialogTitle><AlertDialogDescription>هذا الإجراء لا يمكن التراجع عنه.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2"><AlertDialogCancel>إلغاء</AlertDialogCancel><AlertDialogAction onClick={handleDeleteProduct} className="bg-destructive text-destructive-foreground">حذف</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
